import { useDroppable } from '@dnd-kit/core';
import { TimelineCard, type TimelineCardEntry } from './TimelineCard';
import type { TimelineLayout } from './useTimelineLayout';

export interface TimelineRowEntry extends TimelineCardEntry {
  date: string;
  endDate?: string | null;
  durationMinutes: number;
  userId: string;
}

interface PositionedTimelineRowEntry extends TimelineRowEntry {
  visibleStartIndex: number;
  visibleEndIndex: number;
  spanDays: number;
  stackIndex: number;
}

interface TimelineRowProps {
  userId: string;
  userName: string;
  userInitials: string;
  entries: TimelineRowEntry[];
  layout: TimelineLayout;
  /** Background tint class for the whole row block (issue #33). Member
   * rows use the neutral gray tint (#f4f3f5); the Unassigned row uses a
   * neutral tint. Applied here (cells) and mirrored on the sticky label
   * in TimelineGrid so the whole member block reads as one area. */
  tintClassName: string;
  onEntryClick: (id: string) => void;
  onEntryContextMenu: (id: string, x: number, y: number) => void;
}

/**
 * One horizontal row of day cells. The user label for this row is
 * rendered in a separate sticky column inside TimelineGrid (issue #12
 * follow-up) so position: sticky binds to timeline-root instead of to
 * this row's wrapper.
 */
export function TimelineRow({
  userId,
  entries,
  layout,
  tintClassName,
  onEntryClick,
  onEntryContextMenu,
}: TimelineRowProps) {
  const { rowHeight, stackGap, days } = layout;

  // Place each entry in one lane across its inclusive date range. The card
  // is attached only to its first visible day and spans the remaining day
  // columns, so a multi-day task is one continuous block rather than a
  // duplicated card in every day cell.
  const positionedEntries: PositionedTimelineRowEntry[] = [];
  for (const entry of entries) {
    const visibleStartIndex = days.findIndex((day) => day.date >= entry.date);
    const normalizedEndDate =
      entry.endDate && entry.endDate >= entry.date ? entry.endDate : entry.date;
    const visibleEndIndex = days.reduce(
      (lastIndex, day, index) =>
        day.date <= normalizedEndDate ? index : lastIndex,
      -1,
    );

    if (
      visibleStartIndex === -1 ||
      visibleEndIndex === -1 ||
      visibleStartIndex > visibleEndIndex
    ) {
      continue;
    }

    positionedEntries.push({
      ...entry,
      visibleStartIndex,
      visibleEndIndex,
      spanDays: visibleEndIndex - visibleStartIndex + 1,
      stackIndex: 0,
      startDate: entry.date,
      endDate: entry.endDate,
    });
  }

  positionedEntries.sort(
    (a, b) =>
      a.visibleStartIndex - b.visibleStartIndex ||
      b.visibleEndIndex - a.visibleEndIndex,
  );

  // A lane is available as soon as the last card in it ends before the next
  // card starts. This keeps overlapping multi-day cards readable without
  // changing the existing one-day stacking behavior.
  const laneEndIndices: number[] = [];
  for (const entry of positionedEntries) {
    let lane = 0;
    while (laneEndIndices[lane] >= entry.visibleStartIndex) lane += 1;
    entry.stackIndex = lane;
    laneEndIndices[lane] = entry.visibleEndIndex;
  }

  const entriesByDay = new Map<string, PositionedTimelineRowEntry[]>();
  for (const entry of positionedEntries) {
    const day = days[entry.visibleStartIndex]?.date;
    if (!day) continue;
    const bucket = entriesByDay.get(day) ?? [];
    bucket.push(entry);
    entriesByDay.set(day, bucket);
  }

  const rowHeightWithStack =
    Math.max(1, laneEndIndices.length) * rowHeight +
    Math.max(0, laneEndIndices.length - 1) * stackGap;

  return (
    <div
      data-testid="timeline-row"
      data-user-id={userId}
      // The bottom gap replaces the old hairline `border-b`: each member's
      // block is a colored area with a white "respiro" band between blocks
      // (Toggl-style separation — issue #33). The tint itself goes on the
      // CELLS, not this wrapper: the wrapper is only as wide as the
      // scrollport while the day tracks overflow it, so a background here
      // would paint a strip that ends mid-scroll.
      className="mb-4 grid"
      style={{
        gridTemplateColumns: `repeat(${days.length}, minmax(${layout.minDayWidth}px, 1fr))`,
        minHeight: rowHeightWithStack,
      }}
    >
      {days.map((day) => (
        <DayCell
          key={day.date}
          date={day.date}
          isWeekend={day.isWeekend}
          rowHeight={rowHeight}
          stackGap={stackGap}
          rowMinHeight={rowHeightWithStack}
          entries={entriesByDay.get(day.date) ?? []}
          isCompact={layout.isCompact}
          tintClassName={tintClassName}
          onEntryClick={onEntryClick}
          onEntryContextMenu={onEntryContextMenu}
        />
      ))}
    </div>
  );
}

interface DayCellProps {
  date: string;
  isWeekend: boolean;
  rowHeight: number;
  stackGap: number;
  rowMinHeight: number;
  entries: PositionedTimelineRowEntry[];
  isCompact: boolean;
  tintClassName: string;
  onEntryClick: (id: string) => void;
  onEntryContextMenu: (id: string, x: number, y: number) => void;
}

function DayCell({
  date,
  rowHeight,
  stackGap,
  rowMinHeight,
  entries,
  isCompact,
  tintClassName,
  onEntryClick,
  onEntryContextMenu,
}: DayCellProps) {
  const { isOver, setNodeRef } = useDroppable({
    id: `day:${date}`,
    data: { day: date },
  });

  // Cards are absolutely positioned at top = stackIndex * (rowHeight + stackGap).
  // The cell must grow with the count so the second card isn't clipped/overflows
  // into the next row.
  const cellHeight = Math.max(
    rowMinHeight,
    entries.length * rowHeight + Math.max(0, entries.length - 1) * stackGap,
  );

  return (
    <div
      ref={setNodeRef}
      data-testid="timeline-day-cell"
      data-day={date}
      // The member tint lives on the cell (issue #33): day tracks overflow
      // the row wrapper's box, so only a cell-level background spans the
      // full scroll width. The drop highlight replaces the tint while
      // dragging over the cell.
      className={`relative border-r last:border-r-0 ${
        isCompact ? 'border-dashed' : ''
      } ${
        isOver ? 'bg-primary/10' : tintClassName
      }`}
      style={{ minHeight: cellHeight }}
    >
      {entries.map((entry) => (
        <TimelineCard
          key={entry.id}
          entry={{
            id: entry.id,
            title: entry.title,
            projectName: entry.projectName,
            projectColor: entry.projectColor,
            durationMinutes: entry.durationMinutes,
            spanDays: entry.spanDays,
            startDate: entry.startDate,
            endDate: entry.endDate,
          }}
          stackIndex={entry.stackIndex}
          rowHeight={rowHeight}
          stackGap={stackGap}
          onClick={onEntryClick}
          onContextMenu={onEntryContextMenu}
        />
      ))}
    </div>
  );
}
